import React, { useMemo } from 'react';
import AppPageWrapper, {
    AppPageWrapperProps,
} from '../../common/Layout/AppPageWrapper';
import { useNavigate, useParams } from 'react-router-dom';
import CcCluster, {
    CcClusterBase,
    CcClusterTechnology,
} from '../../services/models/CcCluster';
import DbGrowthPage from './DbGrowthPage';
import QueryMonitorPage from '../QueryMonitor/QueryMonitorPage';
import AgentQueryMonitorPage from '../QueryMonitor/AgentQueryMonitorPage';
import BetaRibbon from '../../common/Feedback/BetaRibbon';
import { ClusterConfigProvider } from '../Clusters/Config/ClusterConfigContext';
import AppSectionWrapper from '../../common/Layout/AppSectionWrapper';
import AdvisorResultsList from '../Clusters/Advisors/AdvisorResultsList';
import RunningOperationPage from './RunningOperationPage';
import TransactionLogPage from './TransactionLogPage';
import DBStatusPage from './DBStatus/DBStatusPage';

export default PerformancePage;

export enum PerformancePageSection {
    DB_GROWTH = 'db-growth',
    QUERY_MONITOR = 'query-monitor',
    AGENT_BASED_QUERY_MONITOR = 'agent-based-query-monitor',
    ADVISORS = 'advisors',
    RUNNING_OPERATIONS = 'running-operations',
    TRANSACTION_LOG = 'transaction-log',
    DB_STATUS = 'db-status',
    DB_VARIABLE = 'db-variables',
}

export type PerformancePageProps = AppPageWrapperProps & {
    cluster: CcCluster;
};

function PerformancePage({ cluster, ...rest }: PerformancePageProps) {
    const navigate = useNavigate();
    const {
        section = !cluster.isBase(CcClusterBase.BASE_REDIS)
            ? PerformancePageSection.DB_GROWTH
            : PerformancePageSection.ADVISORS,
    } = useParams<{
        section: PerformancePageSection;
    }>();
    const submenuItems = useMemo(
        () => [
            ...(!cluster.isBase(CcClusterBase.BASE_REDIS)
                ? [
                      {
                          label: 'DB Growth',
                          key: PerformancePageSection.DB_GROWTH,
                          children: <DbGrowthPage cluster={cluster} />,
                      },
                  ]
                : []),
            ...([
                CcClusterTechnology.TECHNOLOGY_MONGODB,
                CcClusterTechnology.TECHNOLOGY_MYSQL,
            ].includes(cluster.getTechnology())
                ? [
                      {
                          label: 'DB Status',
                          key: PerformancePageSection.DB_STATUS,
                          children: (
                              <DBStatusPage
                                  cluster={cluster}
                                  type={PerformancePageSection.DB_STATUS}
                              />
                          ),
                      },
                  ]
                : []),
            ...([
                CcClusterTechnology.TECHNOLOGY_MYSQL,
                CcClusterTechnology.TECHNOLOGY_POSTGRESQL,
            ].includes(cluster.getTechnology())
                ? [
                      {
                          label: 'DB Variables',
                          key: PerformancePageSection.DB_VARIABLE,
                          children: (
                              <DBStatusPage
                                  cluster={cluster}
                                  type={PerformancePageSection.DB_VARIABLE}
                              />
                          ),
                      },
                  ]
                : []),
            ...([
                CcClusterTechnology.TECHNOLOGY_MYSQL,
                CcClusterTechnology.TECHNOLOGY_POSTGRESQL,
            ].includes(cluster.getTechnology())
                ? [
                      {
                          label: 'Query Monitor',
                          key: PerformancePageSection.QUERY_MONITOR,
                          children: <QueryMonitorPage cluster={cluster} />,
                      },
                      {
                          label: (
                              <BetaRibbon margin={5}>
                                  Query Monitor/Agents
                              </BetaRibbon>
                          ),
                          key: PerformancePageSection.AGENT_BASED_QUERY_MONITOR,
                          children: <AgentQueryMonitorPage cluster={cluster} />,
                      },
                  ]
                : []),
            {
                label: 'Advisors',
                key: PerformancePageSection.ADVISORS,
                children: <AdvisorResultsList cluster={cluster} />,
            },
            ...(cluster.getTechnology() ===
            CcClusterTechnology.TECHNOLOGY_MONGODB
                ? [
                      {
                          label: 'Running Operations',
                          key: PerformancePageSection.RUNNING_OPERATIONS,
                          children: <RunningOperationPage cluster={cluster} />,
                      },
                  ]
                : []),
            ...([CcClusterTechnology.TECHNOLOGY_MYSQL].includes(
                cluster.getTechnology()
            )
                ? [
                      {
                          label: 'Transaction Deadlocks',
                          key: PerformancePageSection.TRANSACTION_LOG,
                          children: <TransactionLogPage cluster={cluster} />,
                      },
                  ]
                : []),
        ],
        [cluster]
    );
    if (!submenuItems.find((menu) => menu.key === section)) {
        navigate(
            `/clusters/${cluster.clusterId}/performance/${submenuItems?.[0].key}`
        );
    }
    return (
        <AppPageWrapper className="PerformancePage" noPadding={true} {...rest}>
            <ClusterConfigProvider clusterId={cluster.clusterId}>
                <AppSectionWrapper
                    items={submenuItems}
                    activeSection={section}
                    onNavigate={(key) => {
                        navigate(
                            `/clusters/${cluster.clusterId}/performance/${key}`
                        );
                    }}
                />
            </ClusterConfigProvider>
        </AppPageWrapper>
    );
}
